// ─────────────────────────────────────────────────────────────────────────────
// SECURITY FIX #6: Sanitized Logger
//
// Problem: console.log/error calls throughout the codebase leak:
//   - User email addresses (LabelAdminDashboard, AuthContext)
//   - Profile objects containing role, label_id, access_status
//   - Payment intent IDs (stripe.js)
//   - Full Supabase error objects (which may echo SQL fragments)
//   - label_id values
//
// Solution: A thin logger wrapper that:
//   1. Is a no-op in production builds (import.meta.env.PROD)
//   2. Scrubs PII fields before logging in development
//   3. Never logs tokens, passwords, or full user objects
// ─────────────────────────────────────────────────────────────────────────────

const IS_DEV = import.meta.env.DEV;

// Fields that must never appear in logs
const SCRUB_KEYS = new Set([
  'password', 'token', 'access_token', 'refresh_token',
  'apikey', 'api_key', 'authorization', 'secret',
  'stripe_payment_intent_id', 'stripe_customer_id',
  'raw_app_meta_data', 'raw_user_meta_data',
]);

// PII fields — replaced with '[redacted]' even in dev
const PII_KEYS = new Set([
  'email', 'phone', 'full_name', 'display_name',
  'actor_email', 'applicant_email',
]);

const scrub = (value, depth = 0) => {
  if (depth > 4 || value === null || value === undefined) return value;
  if (typeof value === 'string') return value;
  if (typeof value !== 'object') return value;
  if (Array.isArray(value)) return value.map(v => scrub(v, depth + 1));

  const out = {};
  for (const [k, v] of Object.entries(value)) {
    const key = k.toLowerCase();
    if (SCRUB_KEYS.has(key)) {
      out[k] = '[secret]';
    } else if (PII_KEYS.has(key)) {
      // In dev show first 3 chars only: "jus…"
      out[k] = IS_DEV && typeof v === 'string' && v.length > 3
        ? v.slice(0, 3) + '…'
        : '[redacted]';
    } else {
      out[k] = scrub(v, depth + 1);
    }
  }
  return out;
};

const fmt = (...args) => args.map(a =>
  a && typeof a === 'object' ? scrub(a) : a
);

const logger = {
  /** Debug — dev only, scrubbed */
  debug: (...args) => {
    if (IS_DEV) console.debug('[VD:debug]', ...fmt(...args));
  },
  /** Info — dev only, scrubbed */
  info: (...args) => {
    if (IS_DEV) console.info('[VD:info]', ...fmt(...args));
  },
  /** Warn — dev only, scrubbed */
  warn: (...args) => {
    if (IS_DEV) console.warn('[VD:warn]', ...fmt(...args));
  },
  /**
   * Error — fires in both dev and prod, but:
   *   - In prod: logs only the message string, never the full error object
   *   - In dev:  logs scrubbed error object for debugging
   */
  error: (label, err) => {
    if (IS_DEV) {
      console.error(`[VD:error] ${label}`, scrub(err));
    } else {
      // In production, only log a sanitised message — no stack traces,
      // no user data, no SQL fragments
      const msg = err?.message || String(err) || 'Unknown error';
      console.error(`[VD:error] ${label}: ${msg.slice(0, 120)}`);
    }
  },
};

export default logger;